import { useCallback, useState } from "react";
import * as Sentry from "@sentry/react";
import * as api from "../lib/claimsApi.ts";
import type { ExtractInput } from "../lib/claimsApi.ts";
import { ApiError, RateLimitError, genericApiError, isSentryWorthy } from "../lib/errors";
import type { ClaimWithSource, VerifyResponse } from "../types/claims";

/** Outcome of the collapsed extract-and-verify call. `verified` when exactly one claim we check was
 *  found, `ambiguous` (zero claims, or several we check) for the review screen, `not_verified` when
 *  claims were found and none is one we check, which are shown with their reasons and never
 *  verified; plus the shared error branches. */
export type ExtractAndVerifyOutcome =
  | { status: "verified"; claim: ClaimWithSource; verification: VerifyResponse }
  | { status: "ambiguous"; claims: ClaimWithSource[] }
  | { status: "not_verified"; claims: ClaimWithSource[] }
  | { status: "rate_limited"; retryAfter: number }
  | { status: "error"; error: ApiError };

interface UseExtractAndVerifyResult {
  execute: (input: ExtractInput) => Promise<ExtractAndVerifyOutcome>;
}

export function useExtractAndVerify(): UseExtractAndVerifyResult {
  const [, setPending] = useState(false);

  const execute = useCallback(async (input: ExtractInput): Promise<ExtractAndVerifyOutcome> => {
    setPending(true);
    Sentry.addBreadcrumb({ category: "api", message: "Extract and verify", level: "info" });
    try {
      const data = await api.extractAndVerify(input);
      switch (data.kind) {
        case "verified":
          return { status: "verified", claim: data.claim, verification: data.verification };
        case "not_verified":
          return { status: "not_verified", claims: data.claims };
        case "ambiguous":
          return { status: "ambiguous", claims: data.claims };
      }
    } catch (err) {
      if (err instanceof RateLimitError) {
        return { status: "rate_limited", retryAfter: err.retryAfter ?? 3600 };
      }
      const error = err instanceof ApiError ? err : genericApiError(err);
      if (isSentryWorthy(err)) {
        Sentry.captureException(err, {
          tags: { "error.code": error.code },
          extra: { endpoint: "/claims/extract-and-verify" },
        });
      }
      return { status: "error", error };
    } finally {
      setPending(false);
    }
  }, []);

  return { execute };
}
